import { api } from "./api";
const canonicalize = value => String(value ?? "").toLowerCase().replace(/[^a-z0-9]+/g, "");
const FEATURED_ORG = {
  id: "productCertification",
  backendId: "productCertification",
  slug: "ProductCertification",
  orgName: "ProductCertification.com",
  description: "Helping manufacturers design, certify, and ship products that are safe, compliant, and green — Ship It!",
  logoUrl: "/PlaceholderImgs/checkLogo.png",
  bannerImages: ["/PlaceholderImgs/Buildings.jpg", "/PlaceholderImgs/handshake.jpg", "/PlaceholderImgs/laptop.webp", "/PlaceholderImgs/meeting.webp", "/PlaceholderImgs/reviews.jpg"],
  orgType: ["Certification"],
  orgIndustryFocus: ["Technology"],
  orgFocus: ["Compliance"],
  productFocus: [],
  regionalFocus: ["US", "Canada", "Europe", "Global"],
  establishedYear: new Date().getFullYear(),
  employees: "",
  website: "https://productcertification.com",
  services: [],
  events: [],
  regulations: [],
  infoSection: {
    location: {
      city: "",
      address: null,
      address2: null,
      state: null,
      postalCode: null,
      country: null
    }
  }
};
const toListItem = (org, fallbackId) => {
  const profile = org.profile ?? {};
  const location = profile.infoSection?.location ?? {};
  const contact = profile.infoSection?.contact;
  const social = profile.infoSection?.social;
  const safeId = org.id ?? fallbackId;
  const safeSlug = org.slug ?? org.id ?? fallbackId;
  return {
    backendId: safeId,
    id: safeId,
    slug: safeSlug,
    orgName: profile.orgName ?? org.name ?? "Untitled Organization",
    orgDescripTitle: profile.orgDescripTitle ?? "",
    description: profile.description ?? org.description ?? "",
    orgDescrip: profile.orgDescrip ?? "",
    logoUrl: profile.logoUrl ?? "",
    bannerImages: profile.bannerImages ?? [],
    orgType: profile.orgType ?? [],
    orgIndustryFocus: profile.orgIndustryFocus ?? [],
    orgFocus: profile.orgFocus ?? [],
    productFocus: profile.productFocus ?? [],
    regionalFocus: profile.regionalFocus ?? [],
    establishedYear: typeof profile.establishedYear === "number" ? profile.establishedYear : new Date().getFullYear(),
    employees: profile.employees ?? "",
    website: profile.website ?? "",
    services: profile.services ?? [],
    events: profile.events ?? [],
    regulations: profile.regulations ?? [],
    infoSection: {
      location: {
        city: location.city ?? "",
        address: location.address ?? null,
        address2: location.address2 ?? null,
        state: location.state ?? null,
        postalCode: location.postalCode ?? null,
        country: location.country ?? null
      },
      contact: contact ? {
        title: String(contact.title ?? ""),
        company: String(contact.company ?? ""),
        email: String(contact.email ?? ""),
        phone: String(contact.phone ?? "")
      } : undefined,
      social: social ? {
        title: String(social.title ?? ""),
        facebook: social.facebook,
        instagram: social.instagram,
        twitter: social.twitter,
        linkedIn: social.linkedIn,
        youtube: social.youtube,
        whatsApp: social.whatsApp,
        tikTok: social.tikTok,
        telegram: social.telegram
      } : undefined
    }
  };
};
export const organizationsApi = api.injectEndpoints({
  endpoints: builder => ({
    getOrganizations: builder.query({
      query: () => ({
        url: "/organizations",
        method: "GET"
      }),
      transformResponse: response => {
        const backendList = Array.isArray(response) ? response.map((item, index) => toListItem(item, String(index + 1))) : [];

        // Merge featured/static seed with backend, deduping by slug/id.
        const merged = [];
        const seen = new Set();
        const upsert = org => {
          const keyCandidates = [canonicalize(org.slug), canonicalize(org.id), canonicalize(org.backendId), canonicalize(org.orgName)].filter(Boolean);
          const key = keyCandidates.find(Boolean);
          if (key && seen.has(key)) return;
          if (key) seen.add(key);
          merged.push(org);
        };
        backendList.forEach(upsert);
        return merged;
      },
      providesTags: result => {
        if (!result) return ["Organizations"];
        const ids = result.map(org => org.backendId || org.id || org.slug).filter(Boolean);
        return ["Organizations", ...ids.map(id => ({
          type: "Organizations",
          id
        }))];
      }
    }),
    createSubmission: builder.mutation({
      query: body => ({
        url: "/org-submissions",
        method: "POST",
        body
      }),
      invalidatesTags: ["OrgSubmissions"]
    }),
    createGuestSubmission: builder.mutation({
      query: body => ({
        url: "/public/org-submissions/guest",
        method: "POST",
        body
      }),
      invalidatesTags: ["OrgSubmissions"]
    }),
    deleteGuestOrgSubmission: builder.mutation({
      query: id => ({
        url: `/admin/org-submissions/guest/${id}`,
        method: "DELETE"
      }),
      invalidatesTags: ["OrgSubmissions"]
    }),
    getPendingSubmissions: builder.query({
      query: () => ({
        url: "/admin/org-submissions",
        method: "GET"
      }),
      providesTags: ["OrgSubmissions"]
    }),
    approveSubmission: builder.mutation({
      query: id => ({
        url: `/admin/org-submissions/${id}/approve`,
        method: "POST"
      }),
      invalidatesTags: ["Organizations", "OrgSubmissions"]
    }),
    rejectSubmission: builder.mutation({
      query: ({
        submissionId,
        ...body
      }) => ({
        url: `/admin/org-submissions/${submissionId}/reject`,
        method: "POST",
        body
      }),
      invalidatesTags: ["OrgSubmissions", "Tasks"]
    }),
    getAdminOrgDeactivationHistory: builder.query({
      query: slug => ({
        url: `/admin/org-submissions/deactivation-history/${encodeURIComponent(slug)}`,
        method: "GET"
      })
    }),
    getTasks: builder.query({
      query: () => ({
        url: "/admin/org-submissions/tasks",
        method: "GET"
      }),
      providesTags: ["Tasks"]
    }),
    addTaskMessage: builder.mutation({
      query: ({
        submissionId,
        editId,
        body,
        replyToMessageId,
        asAdmin
      }) => ({
        url: `${asAdmin ? "/admin" : ""}/org-submissions/${submissionId}/tasks/${editId}/messages`,
        method: "POST",
        body: {
          body,
          replyToMessageId
        }
      }),
      invalidatesTags: ["Tasks"]
    }),
    getSubmissionTasks: builder.query({
      query: submissionId => ({
        url: `/org-submissions/${submissionId}/tasks`,
        method: "GET"
      }),
      providesTags: result => {
        if (!result) return ["Tasks"];
        return ["Tasks", ...result.map(r => ({
          type: "Tasks",
          id: r.id
        }))];
      }
    }),
    getMySubmissions: builder.query({
      query: () => ({
        url: "/org-submissions/mine",
        method: "GET"
      }),
      providesTags: ["OrgSubmissions"]
    }),
    discardSubmission: builder.mutation({
      query: submissionId => ({
        url: `/org-submissions/${submissionId}/discard`,
        method: "PATCH"
      }),
      invalidatesTags: ["OrgSubmissions"]
    }),
    getOrgDeactivationHistory: builder.query({
      query: slug => ({
        url: `/org-submissions/deactivation-history/${encodeURIComponent(slug)}`,
        method: "GET"
      })
    }),
    deactivateOrganization: builder.mutation({
      query: id => ({
        url: `/organizations/${id}/deactivate`,
        method: "PATCH"
      }),
      invalidatesTags: ["Organizations"]
    }),
    getOrganizationContentCounts: builder.query({
      // Cache-first: counts only change when an entity version bumps,
      // so the localStorage copy (stamped with orgs/events/articles/
      // regulations versions at write time) is authoritative until a
      // bump — fresh sessions serve it with zero backend requests.
      async queryFn(_arg, _api, _extra, baseQuery) {
        const ls = await import("@/utils/localStore");
        const cached = ls.peekContentCountsCache();
        if (cached) return {
          data: cached
        };
        // Stamp captured BEFORE the fetch: a version bump landing
        // mid-flight must not be stamped onto a response computed
        // before it.
        const stamp = ls.getContentCountsWriteStamp();
        const res = await baseQuery({
          url: "/organizations/content-counts",
          method: "GET"
        });
        if (res.error) return {
          error: res.error
        };
        const data = res.data;
        ls.setContentCountsCache(data, stamp);
        return {
          data
        };
      },
      // Invalidated from Navigation's version checks: any bump of the
      // four count-driving entities refetches the counts, closing the
      // hard-load race where the cache validates against pre-bump keys.
      providesTags: ["OrgContentCounts"],
      keepUnusedDataFor: 86400 // 24 hours — cache lives until explicit refresh
    }),
    // ── Organization members (owner/manager/associate access levels) ──
    createPromotionBid: builder.mutation({
      query: ({
        orgIdOrSlug,
        ...body
      }) => ({
        url: `/organizations/${encodeURIComponent(orgIdOrSlug)}/promotion-bids`,
        method: "POST",
        body
      }),
      invalidatesTags: (r, e, {
        orgIdOrSlug
      }) => [{
        type: "OrgBids",
        id: orgIdOrSlug
      },
      // Type-only tag refreshes the admin bid queue as well.
      "OrgBids"]
    }),
    getOrgPromotionBids: builder.query({
      query: orgIdOrSlug => ({
        url: `/organizations/${encodeURIComponent(orgIdOrSlug)}/promotion-bids`,
        method: "GET"
      }),
      providesTags: (r, e, orgIdOrSlug) => [{
        type: "OrgBids",
        id: orgIdOrSlug
      }]
    }),
    withdrawPromotionBid: builder.mutation({
      query: ({
        orgIdOrSlug,
        bidId
      }) => ({
        url: `/organizations/${encodeURIComponent(orgIdOrSlug)}/promotion-bids/${bidId}`,
        method: "DELETE"
      }),
      invalidatesTags: (r, e, {
        orgIdOrSlug
      }) => [{
        type: "OrgBids",
        id: orgIdOrSlug
      },
      // Type-only tag refreshes the admin bid queue as well.
      "OrgBids"]
    }),
    getOrgActivity: builder.query({
      query: orgIdOrSlug => ({
        url: `/organizations/${encodeURIComponent(orgIdOrSlug)}/activity`,
        method: "GET"
      }),
      // Tagged with OrgDrafts so draft reviews (which change
      // submission statuses) refresh the activity table too.
      providesTags: (r, e, orgIdOrSlug) => [{
        type: "OrgDrafts",
        id: orgIdOrSlug
      }],
      keepUnusedDataFor: 30
    }),
    getOrgMembers: builder.query({
      query: orgIdOrSlug => ({
        url: `/organizations/${encodeURIComponent(orgIdOrSlug)}/members`,
        method: "GET"
      }),
      providesTags: (_r, _e, orgIdOrSlug) => [{
        type: "OrgMembers",
        id: orgIdOrSlug
      }]
    }),
    // ── Owner draft workflow (manager submissions) ──
    getOrgDrafts: builder.query({
      query: orgIdOrSlug => ({
        url: `/organizations/${encodeURIComponent(orgIdOrSlug)}/drafts`,
        method: "GET"
      }),
      providesTags: (_r, _e, orgIdOrSlug) => [{
        type: "OrgDrafts",
        id: orgIdOrSlug
      }]
    }),
    updateOrgDraftSettings: builder.mutation({
      query: ({
        orgIdOrSlug,
        autoApproveManagerDrafts
      }) => ({
        url: `/organizations/${encodeURIComponent(orgIdOrSlug)}/drafts/settings`,
        method: "PATCH",
        body: {
          autoApproveManagerDrafts
        }
      }),
      invalidatesTags: (_r, _e, {
        orgIdOrSlug
      }) => [{
        type: "OrgDrafts",
        id: orgIdOrSlug
      }]
    }),
    reviewOrgDraft: builder.mutation({
      query: ({
        orgIdOrSlug,
        submissionId,
        decision
      }) => ({
        url: `/organizations/${encodeURIComponent(orgIdOrSlug)}/drafts/${submissionId}/${decision}`,
        method: "PATCH"
      }),
      invalidatesTags: (_r, _e, {
        orgIdOrSlug
      }) => [{
        type: "OrgDrafts",
        id: orgIdOrSlug
      }]
    }),
    inviteOrgMember: builder.mutation({
      query: ({
        orgIdOrSlug,
        email,
        accessLevel
      }) => ({
        url: `/organizations/${encodeURIComponent(orgIdOrSlug)}/members`,
        method: "POST",
        body: {
          email,
          accessLevel
        }
      }),
      invalidatesTags: (_r, _e, {
        orgIdOrSlug
      }) => [{
        type: "OrgMembers",
        id: orgIdOrSlug
      }]
    }),
    updateOrgMember: builder.mutation({
      query: ({
        orgIdOrSlug,
        memberId,
        accessLevel
      }) => ({
        url: `/organizations/${encodeURIComponent(orgIdOrSlug)}/members/${memberId}`,
        method: "PATCH",
        body: {
          accessLevel
        }
      }),
      invalidatesTags: (_r, _e, {
        orgIdOrSlug
      }) => [{
        type: "OrgMembers",
        id: orgIdOrSlug
      }]
    }),
    revokeOrgMember: builder.mutation({
      query: ({
        orgIdOrSlug,
        memberId
      }) => ({
        url: `/organizations/${encodeURIComponent(orgIdOrSlug)}/members/${memberId}`,
        method: "DELETE"
      }),
      invalidatesTags: (_r, _e, {
        orgIdOrSlug
      }) => [{
        type: "OrgMembers",
        id: orgIdOrSlug
      }]
    })
  }),
  overrideExisting: true
});

/** A row in an organization's member list (backend MemberView). */

/** The bidder's own view — amounts are visible only to their org + admins. */

/** One row of the org-scoped submission activity feed. */

export const {
  useGetOrganizationsQuery,
  useCreateSubmissionMutation,
  useCreateGuestSubmissionMutation,
  useDeleteGuestOrgSubmissionMutation,
  useGetPendingSubmissionsQuery,
  useApproveSubmissionMutation,
  useRejectSubmissionMutation,
  useGetTasksQuery,
  useAddTaskMessageMutation,
  useGetSubmissionTasksQuery,
  useGetMySubmissionsQuery,
  useDiscardSubmissionMutation,
  useGetOrgDeactivationHistoryQuery,
  useGetAdminOrgDeactivationHistoryQuery,
  useDeactivateOrganizationMutation,
  useGetOrganizationContentCountsQuery,
  useCreatePromotionBidMutation,
  useGetOrgPromotionBidsQuery,
  useWithdrawPromotionBidMutation,
  useGetOrgActivityQuery,
  useGetOrgMembersQuery,
  useGetOrgDraftsQuery,
  useUpdateOrgDraftSettingsMutation,
  useReviewOrgDraftMutation,
  useInviteOrgMemberMutation,
  useUpdateOrgMemberMutation,
  useRevokeOrgMemberMutation
} = organizationsApi;